/**
 * Zone Themes Configuration
 *
 * Phase 4.3.3 Revision: Simplified platform system
 * - All platforms are rounded rectangles (reliable physics)
 * - Colors are FIXED by surface type (clear gameplay distinction)
 * - Zones provide decoration variety, not color/shape changes
 */

import { SurfaceType } from '../config/constants';

/**
 * Surface Type Color Palettes
 * Colors are FIXED by surface type for gameplay clarity
 */
export const SURFACE_TYPE_COLORS = {
  [SurfaceType.NORMAL]: [0x8b4513, 0xa0522d, 0xcd853f], // Browns and tans (earth)
  [SurfaceType.STICKY]: [0x2e7d32, 0x388e3c, 0x4caf50], // Dark green, green, light green
  [SurfaceType.SLIPPERY]: [0x1976d2, 0x2196f3, 0x42a5f5], // Dark blue, blue, light blue (ice)
  [SurfaceType.BOUNCY]: [0xe65100, 0xff6f00, 0xff9800], // Dark orange, orange, light orange
  [SurfaceType.ANGLED]: [0x616161, 0x757575, 0x9e9e9e], // Dark gray, gray, light gray (stone)
};

/**
 * Get base color for a surface type
 */
export function getSurfaceTypeColor(surfaceType: SurfaceType, variation: number = 0): number {
  const colors = SURFACE_TYPE_COLORS[surfaceType] || SURFACE_TYPE_COLORS[SurfaceType.NORMAL];
  const index = Math.abs(variation) % colors.length;
  return colors[index] || colors[0] || 0x808080;
}

/**
 * Get darker shade for texture overlay
 */
export function getSurfaceTypeDarkColor(surfaceType: SurfaceType): number {
  const baseColor = getSurfaceTypeColor(surfaceType, 0);
  // Darken by reducing RGB values by 30%
  const r = Math.floor(((baseColor >> 16) & 0xff) * 0.7);
  const g = Math.floor(((baseColor >> 8) & 0xff) * 0.7);
  const b = Math.floor((baseColor & 0xff) * 0.7);
  return (r << 16) | (g << 8) | b;
}

/**
 * Decoration configuration
 */
export interface DecoratorConfig {
  type: string;
  chance: number; // Probability (0-1)
  surfaceOnly?: SurfaceType; // Optional: only apply to specific surface type
}

/**
 * Zone Theme (simplified - only decorations and background)
 */
export interface ZoneTheme {
  id: number;
  name: string;
  heightRange: [number, number];
  backgroundColor: number;
  decorations: DecoratorConfig[];
}

export const ZONE_THEMES: ZoneTheme[] = [
  {
    id: 1,
    name: 'Forest Floor',
    heightRange: [0, 2000],
    backgroundColor: 0x2d4a3e, // Dark forest green
    decorations: [
      { type: 'tiny-flower', chance: 0.05 },
      { type: 'small-frog', chance: 0.02, surfaceOnly: SurfaceType.STICKY },
      { type: 'firefly', chance: 0.03 },
      { type: 'dewdrop', chance: 0.08 },
      { type: 'fallen-leaf', chance: 0.05, surfaceOnly: SurfaceType.NORMAL },
      { type: 'mushroom-buddy', chance: 0.04, surfaceOnly: SurfaceType.NORMAL },
    ],
  },
  {
    id: 2,
    name: 'Tree Canopy',
    heightRange: [2000, 4000],
    backgroundColor: 0x5a7a4a, // Dark olive green (lighter than forest floor)
    decorations: [],
  },
  {
    id: 3,
    name: 'Sky Realm',
    heightRange: [4000, 6500],
    backgroundColor: 0x5a7a9d, // Deep sky blue
    decorations: [],
  },
  {
    id: 4,
    name: 'Starlit Night',
    heightRange: [6500, 8500],
    backgroundColor: 0x3d2e5f, // Deep purple/night sky
    decorations: [],
  },
  {
    id: 5,
    name: 'Golden Summit',
    heightRange: [8500, 10000],
    backgroundColor: 0x8d7330, // Dark gold/bronze
    decorations: [],
  },
  {
    id: 6,
    name: 'Ultimate Challenge',
    heightRange: [10000, 11500],
    backgroundColor: 0xb87700, // Dark amber/orange
    decorations: [],
  },
];

/**
 * Get theme for a specific zone ID
 */
export function getZoneTheme(zoneId: number): ZoneTheme | undefined {
  return ZONE_THEMES.find(theme => theme.id === zoneId);
}

/**
 * Get zone ID from world Y position
 */
export function getZoneIdFromY(y: number): number {
  for (const theme of ZONE_THEMES) {
    if (y >= theme.heightRange[0] && y < theme.heightRange[1]) {
      return theme.id;
    }
  }
  // Default to last zone if beyond range
  const lastZone = ZONE_THEMES[ZONE_THEMES.length - 1];
  return lastZone ? lastZone.id : 1; // Fallback to Zone 1 if no zones defined
}
